import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { getApiClient } from "@punchup/utils/apiRegistry";
import { logException } from "@punchup/sentry";

interface WaitlistApiResponse {
  waitlistEntries: WaitlistRow[];
  uniqueShowNames: string[];
}

interface WaitlistRow {
  id: string;
  created_at: string;
  show_name: string | null;
  email: string | null;
  phone: string | null;
  receive_updates: boolean | null;
  show_id: string;
  owner_id: string | null;
  authenticated: boolean | null;
  is_venue: boolean | null;
  comedian_shows?: {
    id: string;
    comedian_id?: string | null;
    datetime: string | null;
    venue?: {
      name: string;
      city: string;
      state: string;
    };
  } | null;
}

interface WaitlistApiParams {
  ownerId: string;
  isVenue: boolean;
  showName?: string;
  searchTerm?: string;
  supabase: SupabaseClient<Database>;
}

export async function getWaitlistData({
  ownerId,
  isVenue,
  showName,
  searchTerm,
  supabase,
}: WaitlistApiParams): Promise<WaitlistApiResponse> {
  let query = supabase
    .from("waitlist")
    .select(
      `
      *,
      comedian_shows!waitlist_show_id_fkey (
        id,
        comedian_id,
        datetime,
        venues (
          name,
          city,
          state
        )
      )
    `
    )
    .eq("owner_id", ownerId);

  if (isVenue !== undefined) {
    query = query.eq("is_venue", isVenue);
  }

  if (showName) {
    query = query.eq("show_name", showName);
  }

  if (searchTerm) {
    query = query.or(
      `show_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`
    );
  }

  query = query.order("created_at", { ascending: false });

  const { data: waitlistEntries, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch waitlist entries: ${error.message}`);
  }

  // Get unique show names for this owner
  let showNamesQuery = supabase
    .from("waitlist")
    .select("show_name")
    .eq("owner_id", ownerId)
    .not("show_name", "is", null);

  if (isVenue !== undefined) {
    showNamesQuery = showNamesQuery.eq("is_venue", isVenue);
  }

  const { data: showNamesData, error: showNamesError } = await showNamesQuery;

  if (showNamesError) {
    throw new Error(`Failed to fetch show names: ${showNamesError.message}`);
  }

  const uniqueShowNames = Array.from(
    new Set(showNamesData.map((item: any) => item.show_name).filter(Boolean))
  ).sort();

  return {
    waitlistEntries: waitlistEntries || [],
    uniqueShowNames,
  };
}

export async function createWaitlistEntry({
  showName,
  receiveUpdates,
  showId,
  ownerId,
  isVenue,
}: {
  showName: string;
  receiveUpdates?: boolean;
  showId: string;
  ownerId: string;
  isVenue: boolean;
}): Promise<{ message: string }> {
  try {
    const { data } = await getApiClient().post<{ data: { message: string } }>(
      "/api/v2/user/waitlist",
      {
        showName,
        receiveUpdates,
        showId,
        ownerId,
        isVenue,
      }
    );

    return data;
  } catch (error) {
    logException(error, {
      tags: {
        showId,
        ownerId,
        isVenue,
      },
    });
    throw error;
  }
}

export async function removeWaitlistEntry({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<{ message: string }> {
  try {
    return await getApiClient().del<{ message: string }>(
      "/api/v2/user/waitlist",
      {
        subscriptionId,
      }
    );
  } catch (error) {
    logException(error, {
      tags: {
        subscriptionId,
      },
    });
    throw error;
  }
}

export type { WaitlistRow, WaitlistApiResponse, WaitlistApiParams };
